import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';

function SkeletonBlock({ className }) {
  return <div className={`bg-[var(--color-muted)] animate-pulse rounded ${className}`} />;
}

export default function WorkoutDietPlans({ memberId }) {
  const [activeTab, setActiveTab] = useState('workout');
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [dietPlan, setDietPlan] = useState(null);
  const [trainerName, setTrainerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [completedExercises, setCompletedExercises] = useState({});

  const fetchPlans = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [wpRes, dpRes] = await Promise.all([
        api.get('/workout-plans', { params: { member_id: memberId } }),
        api.get('/diet-plans', { params: { member_id: memberId } }),
      ]);
      const wpData = (wpRes?.data ?? [])?.[0] ?? null;
      const dpData = (dpRes?.data ?? [])?.[0] ?? null;
      setWorkoutPlan(wpData);
      setDietPlan(dpData);
      if (wpData?.trainer_name) setTrainerName(wpData?.trainer_name);
    } catch (err) {
      setError('Failed to load plans. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const toggleDay = (dayIdx) => setExpandedDays(prev => ({ ...prev, [dayIdx]: !prev?.[dayIdx] }));
  const toggleExercise = (dayIdx, exIdx) => {
    const key = `${dayIdx}-${exIdx}`;
    setCompletedExercises(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const exercises = workoutPlan?.exercises ?? [];
  const meals = dietPlan?.meals ?? [];
  const calorieGoal = dietPlan?.total_calories ?? 2000;
  const totalCalories = meals?.reduce((s, m) => s + (Number(m?.calories) ?? 0), 0);
  const calorieProgress = calorieGoal > 0 ? Math.min(100, Math.round((totalCalories / calorieGoal) * 100)) : 0;

  const mealIcons = {
    Breakfast: 'Sunrise',
    Lunch: 'Sun',
    Dinner: 'Moon',
    Snack: 'Apple',
    Snacks: 'Apple',
    'Pre-Workout': 'Zap',
    'Post-Workout': 'Dumbbell',
  };

  const mealOrder = ['Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Snack', 'Pre-Workout', 'Post-Workout'];
  const sortedMeals = [...meals]?.sort((a, b) => {
    const ai = mealOrder?.indexOf(a?.time);
    const bi = mealOrder?.indexOf(b?.time);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">My Plans</h3>
        <div className="flex bg-[var(--color-muted)] rounded-[var(--radius-sm)] p-1 gap-1">
          {['workout', 'diet']?.map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-3 py-1.5 rounded text-xs font-medium font-[var(--font-caption)] transition-all duration-200 capitalize ${
                activeTab === t ? 'bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm' : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
              }`}
            >
              {t === 'workout' ? '🏋️ Workout' : '🥗 Diet'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3]?.map(i => <SkeletonBlock key={i} className="h-16" />)}
        </div>
      ) : error ? (
        <div className="flex items-center justify-center gap-2 py-8">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
        </div>
      ) : activeTab === 'workout' ? (
        workoutPlan ? (
          <div className="flex flex-col gap-3">
            {/* Plan Header */}
            <div className="flex items-center gap-2 p-3 bg-[var(--color-primary)]/5 border border-[var(--color-primary)]/20 rounded-[var(--radius-sm)]">
              <Icon name="Dumbbell" size={14} color="var(--color-primary)" />
              <span className="text-xs text-[var(--color-foreground)] font-[var(--font-caption)]">
                <span className="font-semibold">{workoutPlan?.title}</span>
                {trainerName && <> · Trainer: <span className="font-semibold">{trainerName}</span></>}
              </span>
            </div>

            {exercises?.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <Icon name="Dumbbell" size={28} color="var(--color-muted-foreground)" />
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No exercises assigned yet</p>
              </div>
            ) : (
              exercises?.map((dayObj, dayIdx) => {
                const isExpanded = expandedDays?.[dayIdx] ?? false;
                const dayExercises = dayObj?.exercises ?? [];
                const completedCount = dayExercises?.filter((_, exIdx) => completedExercises?.[`${dayIdx}-${exIdx}`])?.length;
                return (
                  <div key={dayIdx} className="border border-[var(--color-border)] rounded-[var(--radius-sm)] overflow-hidden">
                    <button
                      onClick={() => toggleDay(dayIdx)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-[var(--color-muted)] hover:bg-[var(--color-muted)]/80 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">{dayObj?.day}</span>
                        {completedCount > 0 && (
                          <span className="text-xs bg-[var(--color-success)]/10 text-[var(--color-success)] px-2 py-0.5 rounded-full font-[var(--font-caption)]">
                            {completedCount}/{dayExercises?.length} done
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[var(--color-primary)] font-medium font-[var(--font-caption)]">{dayExercises?.length} exercises</span>
                        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} color="var(--color-muted-foreground)" />
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="p-3 flex flex-col gap-2">
                        {dayExercises?.map((ex, exIdx) => {
                          const key = `${dayIdx}-${exIdx}`;
                          const done = completedExercises?.[key];
                          return (
                            <button
                              key={exIdx}
                              onClick={() => toggleExercise(dayIdx, exIdx)}
                              className={`flex items-center gap-3 p-2.5 rounded-[var(--radius-sm)] text-left transition-all duration-200 w-full ${
                                done ? 'bg-[var(--color-success)]/10 border border-[var(--color-success)]/20' : 'bg-[var(--color-background)] border border-[var(--color-border)] hover:border-[var(--color-primary)]/30'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                done ? 'bg-[var(--color-success)] border-[var(--color-success)]' : 'border-[var(--color-border)]'
                              }`}>
                                {done && <Icon name="Check" size={11} color="#fff" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-[var(--font-caption)] ${
                                  done ? 'line-through text-[var(--color-muted-foreground)]' : 'text-[var(--color-foreground)]'
                                }`}>{ex?.name}</span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                                {ex?.sets && ex?.reps && (
                                  <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{ex?.sets}×{ex?.reps}</span>
                                )}
                                {ex?.rest && (
                                  <span className="text-xs bg-[var(--color-muted)] text-[var(--color-foreground)] px-1.5 py-0.5 rounded font-[var(--font-caption)]">
                                    {ex?.rest}s rest
                                  </span>
                                )}
                                {ex?.weight && (
                                  <span className="text-xs bg-[var(--color-primary)]/10 text-[var(--color-primary)] px-1.5 py-0.5 rounded font-[var(--font-caption)]">{ex?.weight}</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-8">
            <Icon name="Dumbbell" size={28} color="var(--color-muted-foreground)" />
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No workout plan assigned yet</p>
            <p className="text-xs text-[var(--color-muted-foreground)]/70 font-[var(--font-caption)]">Ask your trainer to assign a workout plan</p>
          </div>
        )
      ) : (
        dietPlan ? (
          <div className="flex flex-col gap-3">
            {/* Plan Header */}
            <div className="flex items-center gap-2 p-3 bg-[var(--color-accent)]/5 border border-[var(--color-accent)]/20 rounded-[var(--radius-sm)]">
              <Icon name="Salad" size={14} color="var(--color-accent)" />
              <span className="text-xs font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">{dietPlan?.title}</span>
            </div>

            {/* Calorie Progress */}
            <div className="p-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">Daily Calories</span>
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{totalCalories} / {calorieGoal} kcal</span>
              </div>
              <div className="h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${calorieProgress}%`, backgroundColor: calorieProgress > 100 ? 'var(--color-error)' : 'var(--color-primary)' }}
                />
              </div>
              <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">{calorieProgress}% of daily goal</p>
            </div>

            {sortedMeals?.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-6">
                <Icon name="UtensilsCrossed" size={28} color="var(--color-muted-foreground)" />
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No meals assigned yet</p>
              </div>
            ) : (
              sortedMeals?.map((meal, idx) => (
                <div key={idx} className="p-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] hover:border-[var(--color-primary)]/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-[var(--radius-sm)] bg-[var(--color-accent)]/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={mealIcons?.[meal?.time] ?? 'UtensilsCrossed'} size={16} color="var(--color-accent)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">{meal?.time}</span>
                        <span className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-caption)]">{meal?.calories} kcal</span>
                      </div>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{meal?.meal ?? meal?.name ?? meal?.food_items}</p>
                      {(meal?.protein || meal?.carbs || meal?.fat) && (
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {meal?.protein && <span className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded font-[var(--font-caption)]">P: {meal?.protein}g</span>}
                          {meal?.carbs && <span className="text-[10px] bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 px-1.5 py-0.5 rounded font-[var(--font-caption)]">C: {meal?.carbs}g</span>}
                          {meal?.fat && <span className="text-[10px] bg-red-500/10 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded font-[var(--font-caption)]">F: {meal?.fat}g</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            <div className="flex items-center justify-between pt-2 border-t border-[var(--color-border)]">
              <span className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">Daily Total</span>
              <span className="text-base font-bold text-[var(--color-primary)] font-[var(--font-heading)]">{totalCalories} kcal</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-8">
            <Icon name="UtensilsCrossed" size={28} color="var(--color-muted-foreground)" />
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No diet plan assigned yet</p>
            <p className="text-xs text-[var(--color-muted-foreground)]/70 font-[var(--font-caption)]">Ask your trainer to assign a diet plan</p>
          </div>
        )
      )}
    </div>
  );
}
